import React, { Component } from "react";
import styled from "styled-components";

import Analytics from "../../../analytics";
import { Icon } from "../../../Components/Icon";
import { PropertyPanelContainer } from "../../../EditorComponents/PropertyPanel";
import { PropertyPanelButton } from "../../../EditorComponents/PropertyPanelButton";
import { ShowDialog } from "../../../react/components/Dialogs/BaseDialog";
import { _ } from "../../../vendor";
import { PropertyPanelHeader } from "../../ElementEditor/PropertyPanelHeader";
import PresentationEditorController from "../../PresentationEditor/PresentationEditorController";
import { AiIconCircle } from "../AiIcon";
import SlideAiController from "./SlideAiController";
import SlideAiDialog from "./SlideAiDialog";
import { SlideAiFeedbackWidget } from "./SlideAiFeedbackWidget";
import SlideAiIdeasList from "./SlideAiIdeasList";
import { withTranslation } from "react-i18next";

const NoIdeasNotice = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    height: 100%;
    width: 100%;
    font-size: 16px;
`;

const ErrorNotice = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;
    height: 100%;
    width: 100%;
    font-size: 16px;
    cursor: pointer;

    .bai-icon {
        font-size: 48px;
        margin-bottom: 10px;
        color: orangered !important;
    }
`;

class SlideAiIdeasPanel extends Component {
    constructor(props) {
        super(props);

        this.state = {
            currentModel: _.cloneDeep(props.currentSlide.attributes),
            isGenerating: false,
            selectedModel: null,
            slideCategory: null,
            slideCategories: [],
        };

        this.hasBeenUnmounted = false;

        this.slideAiIdeasListRef = React.createRef();

        this.handleApplySlidePromiseChain = Promise.resolve();
    }

    componentDidMount() {
        const { currentSlide, currentCanvasController } = this.props;

        PresentationEditorController.hideSelectionLayer();

        currentSlide.update({ hasBeenViewed: true });

        SlideAiController.setCurrentSlide(currentSlide, currentCanvasController);
    }

    componentDidUpdate(prevProps, prevState) {
        const { currentSlide, currentCanvasController } = this.props;

        if (prevProps.currentSlide !== currentSlide) {
            this.setState({
                currentModel: _.cloneDeep(currentSlide.attributes),
                isGenerating: false,
                selectedModelIndex: null,
                slideCategory: null
            });

            SlideAiController.setCurrentSlide(currentSlide, currentCanvasController);
        }
    }

    componentWillUnmount() {
        this.hasBeenUnmounted = true;

        SlideAiController.reset();

        PresentationEditorController.showSelectionLayer();
    }

    shouldComponentUpdate(nextProps) {
        return !nextProps.canvasController?.canvas?.isAnimating;
    }

    setStateAsync = async stateUpdate => {
        await new Promise(resolve => this.setState(stateUpdate, resolve));
    }

    handleRegenerateSlide = () => {
        const { currentSlide, currentCanvasController } = this.props;
        let { ideas } = ShowDialog(SlideAiDialog, { currentSlide, currentCanvasController });
        this.setState({ generatedSlides: ideas });
    }

    handleApplySlide = async model => {
        const { currentSlide, currentCanvasController } = this.props;

        if (currentCanvasController.canvas.isAnimating) {
            currentCanvasController.canvas.stopAnimation();
        }

        this.handleApplySlidePromiseChain = this.handleApplySlidePromiseChain
            .then(async () => {
                currentSlide.update({
                    animations: {},
                    states: model.states,
                    layout: model.layout,
                    template_id: model.template_id,
                    version: 10,
                    migrationVersion: 10.03,
                    textStylesVersion: model.textStylesVersion ?? 2,
                    metadata: {
                        ...model.metadata,
                        needsLayoutProcessing: true
                    }
                }, { replaceKeys: true, silent: true, saveUndo: true });
                currentSlide.trigger("forceSyncClones");

                await currentCanvasController.canvas.handleDataStateUpdate();

                await currentCanvasController.canvas.animate(() => {
                }, false);

                Analytics.AiGeneration.SelectSlideIdea({ slide: currentSlide });
            })
            .catch(() => {
            });
    }

    render() {
        const { t } = this.props;
        const { currentSlide, slideCategories, isGenerating, generateError } = this.props;
        const metadata = currentSlide.getMetadata();

        return (
            <PropertyPanelContainer fullHeight style={{ background: "#444" }}>
                <PropertyPanelHeader>
                    {t("Slide AI")}
                </PropertyPanelHeader>
                <PropertyPanelButton icon={AiIconCircle}
                    title={t("Generate Ideas...")}
                    description={t("Switch any other smart slide and we'll try to preserve your existing content as best we can.")}
                    onClick={this.handleRegenerateSlide}
                    disabled={currentSlide !== SlideAiController.currentSlide}
                />
                {(isGenerating || (slideCategories?.length > 0 && currentSlide === SlideAiController.currentSlide)) &&
                    <SlideAiIdeasList
                        slideWidth={290}
                        onSelect={this.handleApplySlide}
                    />
                }
                {(!isGenerating && !generateError && (!slideCategories || slideCategories?.length === 0)) &&
                    <NoIdeasNotice>
                        {t("No slide ideas have been generated.")}
                    </NoIdeasNotice>
                }
                {(!isGenerating && generateError && (!slideCategories || slideCategories?.length === 0)) &&
                    <ErrorNotice onClick={this.handleRegenerateSlide}>
                        <Icon>warning</Icon>
                        {t("An error occurred while generating slide ideas.")}
                    </ErrorNotice>
                }

                {!metadata.gotUserFeedback && (
                    <SlideAiFeedbackWidget {...this.props} />
                )}
            </PropertyPanelContainer>
        );
    }
}

export default withTranslation()(SlideAiController.withState(SlideAiIdeasPanel));
